import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Skeleton,
  Paper,
  TableContainer,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Pagination,
} from "@mui/material";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import { toast } from "react-toastify";
import ConfirmationDialog from "../../../shared/ui/ConfirmationDialog";
import EmptyState from "../../../shared/ui/EmptyState";
import api from "../../tourPackage/api/api";

const STATUS_COLORS = {
  confirmed: "success",
  pending: "warning",
  cancelled: "error",
};

const STATUS_LABELS = {
  confirmed: "Paid",
  pending: "Pending",
  cancelled: "Cancelled",
};

const SkeletonRow = ({ cols }) => (
  <TableRow>
    {Array.from({ length: cols }).map((_, i) => (
      <TableCell key={i}>
        <Skeleton variant="text" width={i === 0 ? 160 : 100} height={18} />
      </TableCell>
    ))}
  </TableRow>
);

const BookingsTable = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const user = JSON.parse(localStorage.getItem("authUser"));
  const isOwner = user?.role === "tour-owner";
  const colCount = isOwner ? 9 : 8;

  const fetchBookings = async () => {
    if (!user) return;
    const roleParam = isOwner ? "owner" : "distributor";
    try {
      const { data } = await api.get(`/api/bookings/${roleParam}/${user._id}`, {
        params: { page, limit, search, status, sortBy: "createdAt", order: "desc" },
      });
      setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      setTotalPages(data.pages ?? 1);
    } catch (err) {
      console.error("Fetch bookings failed:", err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [user?._id, user?.role, page, search, status]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await api.patch(`/api/bookings/${bookingId}/status`, { status: newStatus });
      fetchBookings();
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  const handleCancelClick = (booking) => { setBookingToCancel(booking); setShowDialog(true); };
  const handleDialogClose = () => { setShowDialog(false); setBookingToCancel(null); };
  const handleDialogConfirm = async () => {
    if (!bookingToCancel) return;
    try {
      await updateStatus(bookingToCancel._id, "cancelled");
      toast.success("Booking cancelled.");
    } catch {
      toast.error("Failed to cancel booking.");
    } finally {
      handleDialogClose();
    }
  };

  const hasFilters = search || status;

  return (
    <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "#F7F8FC" }}>
      <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #F0F0F5", overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #F0F0F5" }}>
          <Typography variant="h6" fontWeight={700} color="text.primary" mb={2}>
            {isOwner ? "Manage Tour Bookings" : "Manage Resale Bookings"}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Search by name or email"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              size="small"
              sx={{ minWidth: 240 }}
            />
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
          </Stack>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tour</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Travelers</TableCell>
                <TableCell>Status</TableCell>
                {isOwner ? (
                  <>
                    <TableCell>Distributor</TableCell>
                    <TableCell>Distributor Email</TableCell>
                    <TableCell>Action</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Owner</TableCell>
                    <TableCell>Owner Email</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cols={colCount} />)
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<ConfirmationNumberOutlinedIcon />}
                      title={hasFilters ? "No bookings match your filters" : "No bookings yet"}
                      description={
                        hasFilters
                          ? "Try clearing your search or changing the status filter."
                          : isOwner
                          ? "Bookings will appear here once customers purchase through your distributor partners."
                          : "Your resale bookings will appear here once customers buy tours through your embedded widget."
                      }
                      action={
                        hasFilters ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setSearch(""); setStatus(""); }}
                          >
                            Clear filters
                          </Button>
                        ) : undefined
                      }
                      py={8}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((b) => (
                  <TableRow key={b._id} hover>
                    <TableCell>{b.tourId?.basicInfo?.tour_name || "—"}</TableCell>
                    <TableCell>{b.userDetails?.name}</TableCell>
                    <TableCell>{b.userDetails?.email}</TableCell>
                    <TableCell>{new Date(b.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{b.travelers}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[b.status] ?? b.status}
                        color={STATUS_COLORS[b.status] ?? "default"}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    {isOwner ? (
                      <>
                        <TableCell>{b.distributorId?.displayName || "—"}</TableCell>
                        <TableCell>{b.distributorId?.email || "—"}</TableCell>
                        <TableCell>
                          {b.status === "confirmed" ? (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleCancelClick(b)}
                            >
                              Cancel
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {b.status === "cancelled" ? "Cancelled" : "Awaiting payment"}
                            </Typography>
                          )}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{b.tourId?.createdBy?.displayName || "—"}</TableCell>
                        <TableCell>{b.tourId?.createdBy?.email || "—"}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && totalPages > 1 && (
          <Box display="flex" justifyContent="center" py={2.5}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      <ConfirmationDialog
        open={showDialog}
        onCancel={handleDialogClose}
        onConfirm={handleDialogConfirm}
        title="Cancel this booking?"
        description="This action is irreversible. The booking will be permanently cancelled."
        confirmText="Yes, Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default BookingsTable;
